/**
 * /policies/loans/eligibility-rules — Session 3c PR 6.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { LoanEligibilityRuleItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function LoanEligibilityRulesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-loan-eligibility-rules", le],
    queryFn: () => session3cApi.listLoanEligibilityRules(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<LoanEligibilityRuleItem>[] = [
    {
      headerKey: "policies.loan_eligibility.col.product",
      label: "Product",
      render: (r) => <span className="font-medium">{r.product_name_en}</span>,
    },
    {
      headerKey: "policies.loan_eligibility.col.tenure",
      label: "Min tenure (months)",
      render: (r) => <span className="font-mono">{r.min_tenure_months.value}</span>,
      traceableValue: (r) => ({ label: t("policies.loan_eligibility.col.tenure", "Min tenure"), stack: r.min_tenure_months }),
    },
    {
      headerKey: "policies.loan_eligibility.col.concurrent",
      label: "Max concurrent loans",
      render: (r) => <span className="font-mono">{r.max_concurrent_loans.value}</span>,
      traceableValue: (r) => ({ label: t("policies.loan_eligibility.col.concurrent", "Max concurrent"), stack: r.max_concurrent_loans }),
    },
    {
      headerKey: "policies.loan_eligibility.col.service",
      label: "Min service (years)",
      render: (r) => <span className="font-mono text-xs">{r.min_service_years}</span>,
    },
  ];

  return (
    <PolicyListPage<LoanEligibilityRuleItem>
      title={t("policies.loan_eligibility.title", "Loan Eligibility Rules")}
      subtitle={t("policies.loan_eligibility.subtitle", "Tenure · grade · prior-loan-status gates per product. Mutaba'aa cross-checks Article 90 cap interaction at apply time.")}
      agent="mutabaa"
      domain="loan_eligibility"
      newButtonLabel={t("policies.loan_eligibility.cta.new", "New Eligibility Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.loan_eligibility.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.loan_eligibility.suggestion.clean_body", "Eligibility rules + minimum tenure + concurrent-loan cap. Article 90 cap watch lives on the Article 90 Config page."),
          primaryActionLabel: t("policies.loan_eligibility.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "loan-eligibility-rules-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.loan_eligibility.title", "Loan Eligibility Rules"),
        window_label: t("policies.loan_eligibility.window", "across active products"),
      }}
    />
  );
}
